import { useId } from "react";
import { IconBaseType } from "@/v2-components/core/icon/type";
import { cn } from "@/libs/utils";

/**
 * 시안 `icons/general/local_mall` (18×18) — PDP 구매 레일의 장바구니 아이콘.
 *
 * ⚠️ **`ShoppingBagIcon`(머티리얼 `shopping_bag`) 과 다른 글리프다.** 손잡이가 가방 윤곽
 * **안쪽**에 있고 몸통이 사각에 가깝다. 처음엔 헤더 장바구니 아이콘을 재사용했는데
 * 디자이너가 이 원본을 지정했다 — 재사용하지 말 것.
 *
 * 원본에서 **색만** 바꿨다: `fill="#21272A"` → `currentColor`. 이 버튼은 hover 에서 배경이
 * 파랗게 차고 내용이 흰색이 되어야 하는데(게스트 결제 버튼과 같은 hover), 색이 하드코딩되면
 * 파란 배경 위에 검은 아이콘이 남는다. 형태(path·viewBox·mask)는 원본 그대로다.
 *
 * ⚠️ mask id 는 `useId()` 로 인스턴스마다 다르게 만든다. 원본의 고정 id
 * (`mask0_29253_9382`)를 그대로 두면 이 아이콘이 **한 화면에 둘 이상** 뜰 때
 * (데스크톱 레일 + 모바일 하단 바 + 컴팩트 바) 문서에 같은 id 가 중복돼, 브라우저가
 * 어느 mask 를 참조할지 보장되지 않는다.
 */
export default function LocalMallIcon({ className }: IconBaseType) {
  const maskId = useId();

  return (
    <svg
      className={cn("size-[18px]", className)}
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <mask
        id={maskId}
        style={{ maskType: "alpha" }}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="18"
        height="18"
      >
        <rect width="18" height="18" fill="#D9D9D9" />
      </mask>
      <g mask={`url(#${maskId})`}>
        <path
          d="M3.98081 16.125C3.60681 16.125 3.28731 15.9926 3.02231 15.7277C2.75744 15.4627 2.625 15.1432 2.625 14.7692V6.23081C2.625 5.85681 2.75744 5.53731 3.02231 5.27231C3.28731 5.00744 3.60681 4.875 3.98081 4.875H5.625C5.625 3.9385 5.95338 3.14187 6.61013 2.48512C7.26688 1.82837 8.0635 1.5 9 1.5C9.9365 1.5 10.7331 1.82837 11.3899 2.48512C12.0466 3.14187 12.375 3.9385 12.375 4.875H14.0192C14.3932 4.875 14.7127 5.00744 14.9777 5.27231C15.2426 5.53731 15.375 5.85681 15.375 6.23081V14.7692C15.375 15.1432 15.2426 15.4627 14.9777 15.7277C14.7127 15.9926 14.3932 16.125 14.0192 16.125H3.98081ZM3.98081 15H14.0192C14.0769 15 14.1298 14.9759 14.1778 14.9278C14.2259 14.8798 14.25 14.8269 14.25 14.7692V6.23081C14.25 6.17306 14.2259 6.12019 14.1778 6.07219C14.1298 6.02406 14.0769 6 14.0192 6H3.98081C3.92306 6 3.87019 6.02406 3.82219 6.07219C3.77406 6.12019 3.75 6.17306 3.75 6.23081V14.7692C3.75 14.8269 3.77406 14.8798 3.82219 14.9278C3.87019 14.9759 3.92306 15 3.98081 15ZM6.75 4.875H11.25C11.25 4.25 11.0313 3.71875 10.5938 3.28125C10.1562 2.84375 9.625 2.625 9 2.625C8.375 2.625 7.84375 2.84375 7.40625 3.28125C6.96875 3.71875 6.75 4.25 6.75 4.875ZM8.98837 10.125C9.8365 10.125 10.5983 9.838 11.2738 9.264C11.9493 8.68988 12.2832 8.03844 12.2754 7.30969C12.2754 7.15481 12.2235 7.02281 12.1196 6.91369C12.0159 6.80456 11.8842 6.75 11.7246 6.75C11.5928 6.75 11.475 6.79544 11.3711 6.88631C11.2673 6.97719 11.1937 7.10387 11.1504 7.26637C11.0418 7.775 10.7827 8.19112 10.3731 8.51475C9.96344 8.83825 9.50187 9 8.98837 9C8.475 9 8.01156 8.83825 7.59806 8.51475C7.18456 8.19112 6.92737 7.775 6.8265 7.26637C6.78325 7.09612 6.7135 6.9675 6.61725 6.8805C6.52113 6.7935 6.40719 6.75 6.27544 6.75C6.11581 6.75 5.98219 6.80456 5.87456 6.91369C5.76681 7.02281 5.71294 7.15481 5.71294 7.30969C5.71294 8.03844 6.04688 8.68988 6.71475 9.264C7.3825 9.838 8.14037 10.125 8.98837 10.125Z"
          fill="currentColor"
        />
      </g>
    </svg>
  );
}
